/**
 * Missions and tasks on the wire: the queries every screen that shows work
 * reads, and the mutations that move it.
 *
 * One key per collection, so the dashboard's task panel and the tasks page cost
 * one request between them, and the event stream's invalidation (`task.*`,
 * `mission.*` in useSystemStatus) reaches both. The decision mutation sends the
 * operator's verdict — "approve", "rework" — and the server maps it onto the
 * machine; the browser never carries a second copy of the transition table, it
 * reads `nextStates` off the task and offers what the server says is legal.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type PageResponse } from '@/api/client'
import type {
  ConductorRun,
  ID,
  MissionResponse,
  MissionState,
  Approval,
  ApprovalList,
  RoutingDecision,
  SupervisorStatus,
  SupervisorTurn,
  TaskEvidence,
  TaskResponse,
  TaskState,
} from '@/types/domain'

/** Every task state that is not an outcome — what "open work" means. */
export const OPEN_TASK_STATES: TaskState[] = [
  'DRAFT',
  'ANALYZING',
  'PLANNED',
  'READY',
  'QUEUED',
  'ROUTING',
  'LEASED',
  'STARTING',
  'RUNNING',
  'WAITING_FOR_AGENT',
  'WAITING_FOR_HUMAN',
  'PAUSED',
  'RATE_LIMITED',
  'AUTH_REQUIRED',
  'HOST_UNREACHABLE',
  'HANDOFF_PENDING',
  'POLICY_BLOCKED',
  'FAILED_RETRYABLE',
  'FAILED_FINAL',
  'REVIEWING',
  'REWORK_REQUIRED',
  'APPROVAL_REQUIRED',
]

export function useMissions(projectId?: ID) {
  const qs = projectId ? `?projectId=${projectId}&limit=200` : '?limit=200'
  return useQuery({
    queryKey: ['missions', projectId ?? ''],
    queryFn: () => api.get<PageResponse<MissionResponse>>(`/missions${qs}`),
    refetchInterval: 15_000,
  })
}

/** Every open task, across missions — what the dashboard and the conductor
 *  read. States are asked for explicitly so an outcome never slips in. */
export function useOpenTasks() {
  return useQuery({
    queryKey: ['tasks', 'open'],
    queryFn: () =>
      api.get<PageResponse<TaskResponse>>(`/tasks?state=${OPEN_TASK_STATES.join(',')}&limit=200`),
    refetchInterval: 15_000,
  })
}

export function useMissionTasks(missionId: ID | undefined) {
  return useQuery({
    queryKey: ['tasks', 'mission', missionId ?? ''],
    queryFn: () => api.get<PageResponse<TaskResponse>>(`/missions/${missionId}/tasks?limit=200`),
    enabled: Boolean(missionId),
    refetchInterval: 15_000,
  })
}

export function useTask(taskId: ID | undefined) {
  return useQuery({
    queryKey: ['tasks', 'one', taskId ?? ''],
    queryFn: () => api.get<TaskResponse>(`/tasks/${taskId}`),
    enabled: Boolean(taskId),
    refetchInterval: 15_000,
  })
}

// The task's evidence: the artifacts Kimene collected and the machine check of
// them against the contract (ADR-0018). Polled while a task is open, because
// collection lands asynchronously after a turn — a test suite outlasts the
// request that triggered it.
export function useTaskEvidence(taskId: ID | undefined, enabled = true) {
  return useQuery({
    queryKey: ['tasks', 'evidence', taskId ?? ''],
    queryFn: () => api.get<TaskEvidence>(`/tasks/${taskId}/artifacts`),
    enabled: Boolean(taskId) && enabled,
    refetchInterval: 15_000,
  })
}

// Simulate a route: ask the router where a task would run, and why, without
// starting anything (ADR-0019). A mutation because it is an on-demand question,
// not a value the screen keeps in step.
export interface SimulateRouteInput {
  taskId?: ID
  projectId?: ID
  archetype?: string
  hostId?: ID
  accountProfileId?: ID
  modelId?: ID
}
export function useSimulateRoute() {
  return useMutation({
    mutationFn: (input: SimulateRouteInput) => api.post<RoutingDecision>('/routes/simulate', input),
  })
}

export interface CreateMissionInput {
  projectId: ID
  title: string
  objective: string
  priority?: number
  riskLevel?: string
  acceptanceCriteria?: string[]
}

export interface CreateTaskInput {
  title: string
  objective: string
  archetype: string
  priority?: number
  acceptanceCriteria?: string[]
  dependsOn?: ID[]
}

/** The mutations, sharing one invalidation: anything that moves work refreshes
 *  both collections and the overview the conductor's advice rides on. */
export function useWorkMutations() {
  const queryClient = useQueryClient()
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['missions'] })
    void queryClient.invalidateQueries({ queryKey: ['tasks'] })
    void queryClient.invalidateQueries({ queryKey: ['overview'] })
  }
  const createMission = useMutation({
    mutationFn: (input: CreateMissionInput) => api.post<MissionResponse>('/missions', input),
    onSuccess: refresh,
  })
  const setMissionState = useMutation({
    mutationFn: ({ id, state, version }: { id: ID; state: MissionState; version: number }) =>
      api.patch<MissionResponse>(`/missions/${id}`, { state, version }),
    onSuccess: refresh,
  })
  const createTask = useMutation({
    mutationFn: ({ missionId, input }: { missionId: ID; input: CreateTaskInput }) =>
      api.post<TaskResponse>(`/missions/${missionId}/tasks`, input),
    onSuccess: refresh,
  })
  const decide = useMutation({
    mutationFn: ({ id, decision, reason }: { id: ID; decision: string; reason?: string }) =>
      api.post<TaskResponse>(`/tasks/${id}/decision`, { decision, reason }),
    onSuccess: refresh,
  })
  return { createMission, setMissionState, createTask, decide, refresh }
}

/** One approval, and the queue of those still waiting. Polled slowly: a
 *  decision waits for a person, and a person is not refreshed every second. */
export function useApproval(approvalId: ID | undefined) {
  return useQuery({
    queryKey: ['approvals', 'one', approvalId ?? ''],
    queryFn: () => api.get<Approval>(`/approvals/${approvalId}`),
    enabled: Boolean(approvalId),
    refetchInterval: 30_000,
  })
}

export function useOpenApprovals() {
  return useQuery({
    queryKey: ['approvals'],
    queryFn: () => api.get<ApprovalList>('/approvals'),
    refetchInterval: 30_000,
  })
}

/** Decide one. It completes or reworks the task, so the task lists and the
 *  overview's risks are invalidated with it. */
export function useApprovalMutations(taskId?: ID) {
  const queryClient = useQueryClient()
  const decide = useMutation({
    mutationFn: ({ id, decision, note }: { id: ID; decision: string; note?: string }) =>
      api.post<Approval>(`/approvals/${id}/decide`, { decision, note }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['approvals'] })
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      void queryClient.invalidateQueries({ queryKey: ['overview'] })
      if (taskId) void queryClient.invalidateQueries({ queryKey: ['task', taskId] })
    },
  })
  return { decide }
}

/** The chief's desk: whether a decision turn can run, what it last decided, and
 *  the recent decisions themselves. Polled rather than pushed, and a little
 *  faster while one is being thought about — a turn takes tens of seconds and
 *  the panel should not look frozen while it does. */
export function useSupervisorStatus() {
  return useQuery({
    queryKey: ['supervisor-status'],
    queryFn: () => api.get<SupervisorStatus>('/supervisor/status'),
    refetchInterval: (query) => (query.state.data?.deciding ? 3_000 : 30_000),
  })
}

/** Run a decision turn, and apply one an operator approved. Both answer 202 and
 *  finish in the background, so both invalidate rather than trusting a body. */
export function useSupervisorMutations() {
  const queryClient = useQueryClient()
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['supervisor-status'] })
    void queryClient.invalidateQueries({ queryKey: ['tasks'] })
    void queryClient.invalidateQueries({ queryKey: ['sessions'] })
    void queryClient.invalidateQueries({ queryKey: ['overview'] })
  }
  const decide = useMutation({
    mutationFn: () => api.post<SupervisorTurn>('/supervisor/turn', {}),
    onSuccess: refresh,
  })
  const approve = useMutation({
    mutationFn: (turnId: ID) => api.post<SupervisorTurn>(`/supervisor/turns/${turnId}/approve`, {}),
    onSuccess: refresh,
  })
  return { decide, approve, refresh }
}

/** The conductor's recent runs — its plans and how they ended. Polled while a
 *  run is being applied, since the results land on the record as they happen. */
export function useConductorRuns() {
  return useQuery({
    queryKey: ['conductor-runs'],
    queryFn: () => api.get<PageResponse<ConductorRun>>('/conductor/runs'),
    refetchInterval: 5_000,
  })
}

/** Plan and approve. A plan is built on demand and returned; approving applies
 *  it — the server drives the sessions and reports on the run record. */
export function useConductorMutations() {
  const queryClient = useQueryClient()
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['conductor-runs'] })
    void queryClient.invalidateQueries({ queryKey: ['tasks'] })
    void queryClient.invalidateQueries({ queryKey: ['sessions'] })
    void queryClient.invalidateQueries({ queryKey: ['overview'] })
  }
  const plan = useMutation({
    mutationFn: () => api.post<ConductorRun>('/conductor/plan', {}),
    onSuccess: refresh,
  })
  const approve = useMutation({
    mutationFn: (runId: ID) => api.post<ConductorRun>(`/conductor/runs/${runId}/approve`, {}),
    onSuccess: refresh,
  })
  return { plan, approve, refresh }
}
